/**
 * Grid cells that encode Nova's accuracy model visually.
 *
 * Two rules from the plan are enforced here, in the UI layer where the curator
 * actually sees them:
 *
 *   1. A pending value shows a skeleton pulse *inside its own cell*. The grid
 *      never blocks and never shows a spinner over the whole screen.
 *   2. A value the model is unsure about must never look identical to one it is
 *      confident about. Confidence is always visible, never buried in a tooltip.
 */

import { createContext, useContext, useState, type ReactNode } from "react";

type Confidence = number | null;

/** Confidence bands. `null` confidence means a deterministic measurement. */
export function confidenceClass(c: Confidence): string {
  if (c === null) return "conf-exact";
  if (c >= 0.85) return "conf-high";
  if (c >= 0.6) return "conf-medium";
  return "conf-low";
}

export function confidenceLabel(c: Confidence): string {
  if (c === null) return "measured exactly";
  if (c >= 0.85) return `high confidence (${(c * 100).toFixed(0)}%)`;
  if (c >= 0.6) return `medium confidence (${(c * 100).toFixed(0)}%)`;
  return `low confidence (${(c * 100).toFixed(0)}%)`;
}

export function ConfidenceCell<T>({
  value,
  confidence,
  format,
  badge = false,
  width = "",
  title,
  onPick,
}: {
  value: T | null;
  confidence: Confidence;
  format: (v: T) => string;
  badge?: boolean;
  width?: string;
  title?: string;
  /** When given, the value becomes a filter button. */
  onPick?: (v: T) => void;
}) {
  // Still being computed by a background worker.
  if (value === null) {
    return <span className={`skeleton ${width}`} aria-label="analysing" />;
  }

  const text = format(value);
  const cls = confidenceClass(confidence);

  return (
    <span className="val" title={title ?? confidenceLabel(confidence)}>
      <span className={`conf-dot ${cls}`} />
      {onPick ? (
        <button
          className="value-filter"
          onClick={(e) => {
            e.stopPropagation();
            onPick(value);
          }}
        >
          {text}
        </button>
      ) : badge ? (
        <span className="key-badge">{text}</span>
      ) : (
        text
      )}
    </span>
  );
}

/**
 * Genre gets two values on purpose: the broad ancestor written to the standard
 * tag so ordinary players behave, and Nova's precise
 * classification kept alongside it.
 */
export function GenreCell({
  genre,
  subGenre,
}: {
  genre: string | null;
  subGenre: string | null;
}) {
  if (!genre) return <span className="skeleton w-lg" aria-label="classifying" />;

  return (
    <span style={{ display: "inline-flex", gap: 5, alignItems: "center" }}>
      <span className="genre-tag">{genre}</span>
      {subGenre && (
        <span
          className="genre-tag precise"
          title={`Precise classification — written to NOVA_SUBGENRE, while "${genre}" goes to the standard genre field`}
        >
          {subGenre}
        </span>
      )}
    </span>
  );
}

/**
 * A grid value a curator can correct in place.
 *
 * Nova replaced a review queue with this. The queue was a screen that listed
 * every value the engine was unsure about and asked somebody to visit it, which
 * put the correction a long way from the place the mistake is actually noticed —
 * a curator scanning the grid, seeing a tempo that is obviously wrong for a
 * record they know. Editing where you are looking is fewer steps than being
 * routed somewhere, and it does not need the engine to have flagged the value
 * first: plenty of confident answers are still wrong, and the queue could never
 * show those at all.
 *
 * Double-click, or Enter on the focused row. Escape abandons, Enter or blur
 * commits, and a commit that the engine refuses puts the old text back rather
 * than leaving the grid showing something that was never written.
 */
/**
 * Whether double-click edits in place. On in the tag editor; off in the
 * library, where double-click plays the song like in any music player.
 */
export const InlineEdit = createContext(true);

export function EditableCell({
  value,
  placeholder,
  title,
  onCommit,
  children,
}: {
  /** The text an edit starts from. */
  value: string;
  placeholder?: string;
  title?: string;
  /** Returns an error to show, or null when the write landed. */
  onCommit: (next: string) => Promise<string | null>;
  /** How the value looks when it is not being edited. */
  children: ReactNode;
}) {
  const inline = useContext(InlineEdit);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const begin = () => {
    setDraft(value);
    setError(null);
    setEditing(true);
  };

  const commit = async () => {
    if (busy) return;
    setEditing(false);
    if (draft === value) return;
    setBusy(true);
    const failure = await onCommit(draft.trim());
    setBusy(false);
    setError(failure);
  };

  if (editing) {
    return (
      <input
        className="cell-edit"
        autoFocus
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        // Clicks inside the editor must not reach the row, or starting an edit
        // would also change which rows are selected underneath it.
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={(e) => e.stopPropagation()}
        onBlur={() => void commit()}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "Enter") void commit();
          if (e.key === "Escape") {
            setDraft(value);
            setEditing(false);
          }
        }}
      />
    );
  }

  if (!inline) return <>{children}</>;

  return (
    <span
      className={`cell-editable ${busy ? "saving" : ""} ${error ? "bad" : ""}`}
      title={error ?? title ?? "Double-click to edit"}
      onDoubleClick={(e) => {
        e.stopPropagation();
        begin();
      }}
    >
      {children}
    </span>
  );
}
